import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import { getCsrfToken } from "../services/authService";
import "../styles/Checkout.css";

const emptyAddressForm = {
  type: "SHIPPING",
  name: "",
  lastname1: "",
  lastname2: "",
  street: "",
  numberAddress: "",
  apartment: "",
  city: "",
  zipCode: "",
  province: "",
  country: "España",
  phone: "",
  predeterminated: false,
  taxId: "",
  companyName: "",
};

export default function Checkout() {
  const [checkoutToken] = useState(() => crypto.randomUUID());

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { items, totalPrice } = useCart();

  const [addresses, setAddresses] = useState([]);
  const [selectedShippingAddressId, setSelectedShippingAddressId] =
    useState("");
  const [selectedBillingAddressId, setSelectedBillingAddressId] =
    useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] =
  useState("REDSYS_CARD");
  const [showAddressForm, setShowAddressForm] =
    useState(false);
  const [addressForm, setAddressForm] =
    useState(emptyAddressForm);
  const [sameAddressForBilling, setSameAddressForBilling] =
    useState(true);
  const [addressError, setAddressError] = useState("");

  const finalTotal = totalPrice;

  const shippingAddresses = addresses.filter(
    (address) => address.type === "SHIPPING"
  );

  const billingAddresses = addresses.filter(
    (address) => address.type === "BILLING"
  );

  useEffect(() => {
    loadAddresses();
  }, []);

  const handleAddressChange = (event) => {
    const { name, value, type, checked } = event.target;

    setAddressForm((currentForm) => ({
      ...currentForm,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetAddressForm = () => {
    setAddressForm(emptyAddressForm);
    setSameAddressForBilling(true);
    setAddressError("");
    setShowAddressForm(false);
  };

  const loadAddresses = async () => {
    try {
      const response = await fetch("/api/addresses", {
        credentials: "include",
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();

      setAddresses(data);

      const defaultShipping =
        data.find(
          (address) =>
            address.type === "SHIPPING" &&
            address.predeterminated
        ) ||
        data.find(
          (address) => address.type === "SHIPPING"
        );

      const defaultBilling =
        data.find(
          (address) =>
            address.type === "BILLING" &&
            address.predeterminated
        ) ||
        data.find(
          (address) => address.type === "BILLING"
        );

      if (defaultShipping) {
        setSelectedShippingAddressId(defaultShipping.id);
      }

      if (defaultBilling) {
        setSelectedBillingAddressId(defaultBilling.id);
      }
    } catch {
      setError(t("checkout.orderError"));
    }
  };

  const saveCheckoutAddress = async (event) => {
    event.preventDefault();

    setAddressError("");

    if (
      sameAddressForBilling &&
      !addressForm.taxId.trim()
    ) {
      setAddressError(
        t("addresses.taxIdRequired")
      );
      return;
    }

    try {
      const csrf = await getCsrfToken();

      const saveAddress = async (addressData) => {
        return fetch("/api/addresses", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            [csrf.headerName]: csrf.token,
          },
          body: JSON.stringify(addressData),
        });
      };

      const shippingResponse = await saveAddress({
        ...addressForm,
        type: "SHIPPING",
        taxId: null,
        companyName: null,
      });

      if (!shippingResponse.ok) {
        setAddressError(t("addresses.error"));
        return;
      }

      const createdShipping =
        await shippingResponse.json();

      let createdBilling = null;

      if (sameAddressForBilling) {
        const billingResponse = await saveAddress({
          ...addressForm,
          type: "BILLING",
          taxId: addressForm.taxId.trim(),
          companyName:
            addressForm.companyName.trim() || null,
        });

        if (!billingResponse.ok) {
          setAddressError(t("addresses.error"));
          return;
        }

        createdBilling =
          await billingResponse.json();
      }

      await loadAddresses();

      setSelectedShippingAddressId(
        createdShipping.id
      );

      if (createdBilling) {
        setSelectedBillingAddressId(
          createdBilling.id
        );
      } else {
        setSelectedBillingAddressId("");
      }

      resetAddressForm();
    } catch {
      setAddressError(t("addresses.error"));
    }
  };

  const redirectToRedsys = (redsysData) => {
    const form = document.createElement("form");

    form.method = "POST";
    form.action = redsysData.paymentUrl;
    form.style.display = "none";

    const fields = {
      Ds_SignatureVersion: redsysData.signatureVersion,
      Ds_MerchantParameters: redsysData.merchantParameters,
      Ds_Signature: redsysData.signature,
    };

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement("input");

      input.type = "hidden";
      input.name = name;
      input.value = value;

      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  const createOrder = async () => {
    setError("");

    if (items.length === 0) {
      navigate("/cart");
      return;
    }

    if (!selectedShippingAddressId) {
      setError(
        t("checkout.selectShippingAddressError")
      );
      return;
    }

    if (!selectedBillingAddressId) {
      setError(
        t("checkout.selectBillingAddressError")
      );
      return;
    }

    setLoading(true);

    try {
      const csrf = await getCsrfToken();

      const payload = {
        checkoutToken,
        shippingAddressId:
          selectedShippingAddressId,

        billingAddressId: selectedBillingAddressId,

        paymentMethod,

        items: items.map((item) => ({
          productId: item.id,
          quantity: item.quantity,
        })),
      };

      const orderResponse = await fetch(
        "/api/orders",
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            [csrf.headerName]: csrf.token,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!orderResponse.ok) {
        setError(t("checkout.orderError"));
        return;
      }

      const order = await orderResponse.json();

      const redsysResponse = await fetch(
        `/api/payments/redsys/payment/${order.id}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            [csrf.headerName]: csrf.token,
          },
        }
      );

      if (!redsysResponse.ok) {
        setError(t("checkout.orderError"));
        return;
      }

      const redsysData = await redsysResponse.json();

      redirectToRedsys(redsysData);
      return;
    } catch {
      setError(t("checkout.orderError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="checkout-page">
      <section className="checkout-container">
        <h1>{t("checkout.title")}</h1>

        {items.length === 0 ? (
          <div className="checkout-empty">
            <p>{t("checkout.emptyCart")}</p>

            <button
              type="button"
              onClick={() => navigate("/shop")}
            >
              {t("checkout.goToShop")}
            </button>
          </div>
        ) : (
          <div className="checkout-layout">
            <section className="checkout-block">
              <h2>
                {t("checkout.shippingAddress")}
              </h2>

              {addresses.length === 0 &&
              !showAddressForm ? (
                <div className="checkout-no-address">
                  <p>{t("checkout.noAddresses")}</p>

                  <button
                    type="button"
                    onClick={() =>
                      setShowAddressForm(true)
                    }
                  >
                    + {t("addresses.addAddress")}
                  </button>
                </div>
              ) : (
                <>
                  <div className="checkout-addresses">
                    <h3 className="checkout-address-group">
                      {t(
                        "addresses.shippingAddresses"
                      )}
                    </h3>

                    {shippingAddresses.length === 0 ? (
                      <p className="checkout-empty-group">
                        {t(
                          "addresses.noShippingAddresses"
                        )}
                      </p>
                    ) : (
                      shippingAddresses.map(
                        (address) => (
                          <label
                            key={address.id}
                            className={
                              selectedShippingAddressId ===
                              address.id
                                ? "checkout-address selected"
                                : "checkout-address"
                            }
                          >
                            <input
                              type="radio"
                              name="shippingAddress"
                              value={address.id}
                              checked={
                                selectedShippingAddressId ===
                                address.id
                              }
                              onChange={() =>
                                setSelectedShippingAddressId(
                                  address.id
                                )
                              }
                            />

                            <div>
                              <span className="checkout-address-badge shipping">
                                {t(
                                  "addresses.shipping"
                                )}
                              </span>

                              <strong>
                                {address.name}{" "}
                                {address.lastname1}
                              </strong>

                              <p>
                                {address.street},{" "}
                                {address.numberAddress}
                                {address.apartment
                                  ? `, ${address.apartment}`
                                  : ""}
                              </p>

                              <p>
                                {address.zipCode}{" "}
                                {address.city}
                                {address.province
                                  ? `, ${address.province}`
                                  : ""}
                                , {address.country}
                              </p>

                              {address.phone && (
                                <p>
                                  {t(
                                    "addresses.phone"
                                  )}
                                  : {address.phone}
                                </p>
                              )}

                              {address.predeterminated && (
                                <span>
                                  {t(
                                    "addresses.defaultShipping"
                                  )}
                                </span>
                              )}
                            </div>
                          </label>
                        )
                      )
                    )}

                    <>
                      <h3 className="checkout-address-group">
                        {t("addresses.billingAddresses")}
                      </h3>

                      {billingAddresses.length === 0 ? (
                        <p className="checkout-empty-group">
                          {t("addresses.noBillingAddresses")}
                        </p>
                      ) : (
                        billingAddresses.map((address) => (
                          <label
                            key={address.id}
                            className={
                              selectedBillingAddressId === address.id
                                ? "checkout-address selected"
                                : "checkout-address"
                            }
                          >
                            <input
                              type="radio"
                              name="billingAddress"
                              value={address.id}
                              checked={
                                selectedBillingAddressId === address.id
                              }
                              onChange={() =>
                                setSelectedBillingAddressId(address.id)
                              }
                            />

                            <div>
                              <span className="checkout-address-badge billing">
                                {t("addresses.billing")}
                              </span>

                              <strong>
                                {address.name} {address.lastname1}
                              </strong>

                              <p>
                                {address.street}, {address.numberAddress}
                                {address.apartment
                                  ? `, ${address.apartment}`
                                  : ""}
                              </p>

                              <p>
                                {address.zipCode} {address.city}
                                {address.province
                                  ? `, ${address.province}`
                                  : ""}
                                , {address.country}
                              </p>

                              {address.phone && (
                                <p>
                                  {t("addresses.phone")}: {address.phone}
                                </p>
                              )}

                              {address.companyName && (
                                <p>
                                  <strong>
                                    {t("addresses.companyName")}:
                                  </strong>{" "}
                                  {address.companyName}
                                </p>
                              )}

                              {address.taxId && (
                                <p>
                                  <strong>
                                    {t("addresses.taxId")}:
                                  </strong>{" "}
                                  {address.taxId}
                                </p>
                              )}

                              {address.predeterminated && (
                                <span>
                                  {t("addresses.defaultBilling")}
                                </span>
                              )}
                            </div>
                          </label>
                        ))
                      )}
                    </>
                  </div>

                  <button
                    type="button"
                    className="checkout-secondary-btn"
                    onClick={() =>
                      setShowAddressForm(true)
                    }
                  >
                    + {t("addresses.addAddress")}
                  </button>
                </>
              )}

              {showAddressForm && (
                <form
                  className="checkout-address-form"
                  onSubmit={saveCheckoutAddress}
                >
                  <h3>
                    {t("addresses.newAddress")}
                  </h3>

                  <div className="checkout-address-form-grid">
                    <label>
                      {t("addresses.name")}
                      <input
                        name="name"
                        value={addressForm.name}
                        onChange={
                          handleAddressChange
                        }
                        required
                      />
                    </label>

                    <label>
                      {t("addresses.lastname1")}
                      <input
                        name="lastname1"
                        value={
                          addressForm.lastname1
                        }
                        onChange={
                          handleAddressChange
                        }
                        required
                      />
                    </label>

                    <label>
                      {t("addresses.lastname2")}
                      <input
                        name="lastname2"
                        value={
                          addressForm.lastname2
                        }
                        onChange={
                          handleAddressChange
                        }
                      />
                    </label>

                    <label>
                      {t("addresses.street")}
                      <input
                        name="street"
                        value={addressForm.street}
                        onChange={
                          handleAddressChange
                        }
                        required
                      />
                    </label>

                    <label>
                      {t(
                        "addresses.numberAddress"
                      )}
                      <input
                        name="numberAddress"
                        value={
                          addressForm.numberAddress
                        }
                        onChange={
                          handleAddressChange
                        }
                        required
                      />
                    </label>

                    <label>
                      {t("addresses.apartment")}
                      <input
                        name="apartment"
                        value={
                          addressForm.apartment
                        }
                        onChange={
                          handleAddressChange
                        }
                      />
                    </label>

                    <label>
                      {t("addresses.city")}
                      <input
                        name="city"
                        value={addressForm.city}
                        onChange={
                          handleAddressChange
                        }
                        required
                      />
                    </label>

                    <label>
                      {t("addresses.zipCode")}
                      <input
                        name="zipCode"
                        value={
                          addressForm.zipCode
                        }
                        onChange={
                          handleAddressChange
                        }
                        required
                      />
                    </label>

                    <label>
                      {t("addresses.province")}
                      <input
                        name="province"
                        value={
                          addressForm.province
                        }
                        onChange={
                          handleAddressChange
                        }
                        required
                      />
                    </label>

                    <label>
                      {t("addresses.country")}
                      <input
                        name="country"
                        value={
                          addressForm.country
                        }
                        onChange={
                          handleAddressChange
                        }
                        required
                      />
                    </label>

                    <label>
                      {t("addresses.phone")}
                      <input
                        name="phone"
                        value={addressForm.phone}
                        onChange={
                          handleAddressChange
                        }
                        required
                      />
                    </label>
                  </div>

                  {sameAddressForBilling && (
                    <div className="checkout-fiscal-fields">
                      <h3>
                        {t("addresses.billingDetails")}
                      </h3>

                      <div className="checkout-address-form-grid">
                        <label>
                          {t("addresses.companyName")}
                          <input
                            type="text"
                            name="companyName"
                            value={addressForm.companyName}
                            onChange={handleAddressChange}
                            maxLength={255}
                          />
                        </label>

                        <label>
                          {t("addresses.taxId")}
                          <input
                            type="text"
                            name="taxId"
                            value={addressForm.taxId}
                            onChange={handleAddressChange}
                            maxLength={30}
                            required
                          />
                        </label>
                      </div>
                    </div>
                  )}

                  <label className="checkout-same-billing">
                    <input
                      type="checkbox"
                      checked={
                        sameAddressForBilling
                      }
                      onChange={(event) =>
                        setSameAddressForBilling(
                          event.target.checked
                        )
                      }
                    />

                    <span>
                      {t(
                        "checkout.sameBillingAddress"
                      )}
                    </span>
                  </label>

                  <label className="checkout-same-billing">
                    <input
                      type="checkbox"
                      name="predeterminated"
                      checked={
                        addressForm.predeterminated
                      }
                      onChange={
                        handleAddressChange
                      }
                    />

                    <span>
                      {t(
                        "addresses.predeterminated"
                      )}
                    </span>
                  </label>

                  {addressError && (
                    <p className="checkout-error">
                      {addressError}
                    </p>
                  )}

                  <div className="checkout-address-form-actions">
                    <button type="submit">
                      {t("addresses.save")}
                    </button>

                    <button
                      type="button"
                      onClick={resetAddressForm}
                    >
                      {t("addresses.cancel")}
                    </button>
                  </div>
                </form>
              )}
            </section>

            <aside className="checkout-summary">
              <h2>{t("checkout.summary")}</h2>

              <div className="checkout-items">
                {items.map((item) => (
                  <div
                    className="checkout-item"
                    key={item.id}
                  >
                    <span>
                      {item.name} x{item.quantity}
                    </span>

                    <strong>
                      {(
                        Number(item.price) *
                        item.quantity
                      ).toFixed(2)}{" "}
                      €
                    </strong>
                  </div>
                ))}
              </div>

              <div className="checkout-row">
                <span>
                  {t("checkout.subtotal")}
                </span>

                <strong>
                  {totalPrice.toFixed(2)} €
                </strong>
              </div>

              <p className="checkout-price-info">
                {t("checkout.priceIncludes")}
              </p>

              <section className="checkout-payment">
                <h3>{t("checkout.paymentMethod")}</h3>

                <label className="checkout-payment-option">
                  <input
                    type="radio"
                    value="REDSYS_CARD"
                    checked={paymentMethod === "REDSYS_CARD"}
                    onChange={(event) =>
                      setPaymentMethod(event.target.value)
                    }
                  />

                  <span>{t("checkout.card")}</span>
                </label>

                <label className="checkout-payment-option">
                  <input
                    type="radio"
                    value="REDSYS_BIZUM"
                    checked={paymentMethod === "REDSYS_BIZUM"}
                    onChange={(event) =>
                      setPaymentMethod(event.target.value)
                    }
                  />

                  <span>{t("checkout.bizum")}</span>
                </label>

                <label className="checkout-payment-option">
                  <input
                    type="radio"
                    value="REDSYS_TRANSFER"
                    checked={paymentMethod === "REDSYS_TRANSFER"}
                    onChange={(event) =>
                      setPaymentMethod(event.target.value)
                    }
                  />

                  <span>{t("checkout.bankTransfer")}</span>
                </label>
              </section>

              <div className="checkout-total">
                <span>{t("checkout.total")}</span>

                <strong>
                  {finalTotal.toFixed(2)} €
                </strong>
              </div>

              {error && (
                <p className="checkout-error">
                  {error}
                </p>
              )}

              <button
                type="button"
                onClick={createOrder}
                disabled={
                  loading ||
                  shippingAddresses.length === 0
                }
              >
                {loading
                  ? t("checkout.processing")
                  : t("checkout.placeOrder")}
              </button>
            </aside>
          </div>
        )}
      </section>
    </main>
  );
}